import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Calendar,
  Users,
  MapPin,
  Star,
  Clock,
  UserCheck,
  ExternalLink,
  Upload,
  Download,
  FileIcon,
  X,
  Tag,
  Target,
  Share2,
  Loader2,
  Repeat,
  CalendarDays,
  Award,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get the correct download URL for attachments
const getAttachmentUrl = (attachment) => {
  if (attachment.storage_type === "cloud") {
    return `${API}/storage/${attachment.filename}`;
  }
  return `${API}/uploads/${attachment.filename}`;
};

const EVENT_TYPES = [
  { value: "participation", label: "Participation Event" },
  { value: "noteworthy", label: "Noteworthy Event" },
];

const ROLES = [
  { value: "lead", label: "Lead" },
  { value: "contributor", label: "Contributor" },
  { value: "collaborator", label: "Collaborator" },
  { value: "assistant", label: "Assistant" },
  { value: "attendee", label: "Attendee" },
  { value: "consultant", label: "Consultant" },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const FOLLOW_UP_STATUSES = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "waiting", label: "Waiting" },
  { value: "complete", label: "Complete" },
  { value: "closed", label: "Closed" },
];

// Category tags mapped to monthly report sections:
// - Positive: "Positive things that happened this month"
// - Negative, Incident, Policy, Support: "Support topics"
// - Patron Interaction, Technology, Project, Educational Opportunity, Meeting, BAL, Outreach, Collaborative Effort: "Summary of involvement"
// - Contributes to Goals: "Progress to goals"
// - Improvement Idea: "Support topics"
const PRESET_CATEGORY_TAGS = [
  "Positive",
  "Negative",
  "Patron Interaction",
  "Incident",
  "Technology",
  "Project",
  "Educational Opportunity",
  "Meeting",
  "BAL",
  "Policy",
  "Outreach",
  "Collaborative Effort",
  "Contributes to Goals",
  "Support",
  "Improvement Idea"
];

const RECURRENCE_TYPES = [
  { value: "none", label: "Does not repeat" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly_day_of_week", label: "Monthly (e.g., 1st Friday)" },
  { value: "monthly_day_of_month", label: "Monthly (e.g., 15th)" },
  { value: "custom", label: "Custom dates" }
];

// Essential Functions and LA Competencies for staff evaluation
const ESSENTIAL_FUNCTIONS = [
  { value: "Patron Assistance (35%)", label: "Patron Assistance (35%)" },
  { value: "Circulation (20%)", label: "Circulation (20%)" },
  { value: "Technology Instruction (15%)", label: "Technology Instruction (15%)" },
  { value: "Programming (15%)", label: "Programming (15%)" },
  { value: "Collection Maintenance (10%)", label: "Collection Maintenance (10%)" },
  { value: "Other Duties (5%)", label: "Other Duties (5%)" }
];

const LA_COMPETENCIES = [
  { value: "Focuses on the Customer", label: "Focuses on the Customer" },
  { value: "Takes Initiative", label: "Takes Initiative" },
  { value: "Contributes to a Positive Work Environment", label: "Contributes to a Positive Work Environment" },
  { value: "Strategic and Systems Thinking", label: "Strategic and Systems Thinking" },
  { value: "Pursues Personal Development", label: "Pursues Personal Development" },
  { value: "Continuous Improvement", label: "Continuous Improvement" },
  { value: "Takes Pride in the Workplace", label: "Takes Pride in the Workplace" }
];

const WEEK_OF_MONTH_OPTIONS = [
  { value: "first", label: "First" },
  { value: "second", label: "Second" },
  { value: "third", label: "Third" },
  { value: "fourth", label: "Fourth" },
  { value: "last", label: "Last" }
];

const DAYS_OF_WEEK = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" }
];

const initialFormData = {
  title: "",
  description: "",
  event_type: "participation",
  date: "",
  time: "",
  event_date: "",  // Deadline/actual event date
  location: "",
  participants_count: 0,
  highlights: "",
  notes: "",
  role: "lead",
  role_assignee: "",
  priority: "medium",
  follow_up_status: "open",
  category_tags: [],
  linked_goals: [],
  production_items: [],
  suggested_competencies: [],
  // Recurrence
  recurrence: {
    type: "none",
    days_of_week: [],
    week_of_month: "first",
    day_of_week: "friday",
    day_of_month: 1,
    end_type: "occurrences",
    occurrences: 4,
    end_date: "",
    interval: 1,
    custom_dates: []
  }
};

const initialProductionItem = {
  title: "",
  quantity: "",
  due_date: "",
  notes: "",
  lead: ""
};

export const Events = ({ user, viewMode, userLookup = {} }) => {
  const [events, setEvents] = useState([]);
  const [goals, setGoals] = useState([]);
  const [productionTitles, setProductionTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [isEditing, setIsEditing] = useState(false);
  const [categoryTagInput, setCategoryTagInput] = useState("");
  const [formTab, setFormTab] = useState("details");
  const fileInputRef = useRef(null);
  
  // Share dialog state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEvent, setShareEvent] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedShareUsers, setSelectedShareUsers] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  
  // Check if showing all data (supervisor/admin viewing all)
  const isShowingAllData = (user?.role === "supervisor" || user?.is_admin) && viewMode !== "staff";
  
  // Helper to get staff name from userLookup
  const getStaffName = (createdBy) => {
    if (!createdBy || !userLookup[createdBy]) return null;
    return userLookup[createdBy].name;
  };
  
  // Check if current user can edit this event
  const canEditEvent = (event) => {
    if (!event || !user) return false;
    if (user.role === "supervisor" || user.is_admin) return true;
    if (event.created_by === user.id) return true;
    if (event.shared_with?.includes(user.id)) return true;
    return false;
  };
  
  // Sorting states
  const [participationSort, setParticipationSort] = useState("event_date");
  const [noteworthySort, setNoteworthySort] = useState("newest");

  useEffect(() => {
    fetchEvents();
    fetchGoals();
    fetchProductionTitles();
    fetchAllUsers();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data.filter(u => u.status === "approved"));
    } catch (error) {
      console.error("Failed to fetch users for sharing");
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/goals`);
      setGoals(response.data);
    } catch (error) {
      console.error("Failed to fetch goals");
    }
  };

  const fetchProductionTitles = async () => {
    try {
      const response = await axios.get(`${API}/events/production-titles`);
      setProductionTitles(response.data);
    } catch (error) {
      console.error("Failed to fetch production titles");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        participants_count: parseInt(formData.participants_count) || 0,
      };
      
      // Set competency_status to pending if competencies are suggested
      if (submitData.suggested_competencies && submitData.suggested_competencies.length > 0) {
        submitData.competency_status = "pending";
      }

      if (isEditing && selectedEvent) {
        await axios.put(`${API}/events/${selectedEvent.id}`, submitData);
        toast.success("Event updated successfully");
      } else {
        const response = await axios.post(`${API}/events`, submitData);
        setSelectedEvent(response.data);
        toast.success("Event created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(isEditing ? "Failed to update event" : "Failed to create event");
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    try {
      await axios.delete(`${API}/events/${selectedEvent.id}`);
      toast.success("Event deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleEdit = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      date: event.date,
      time: event.time || "",
      event_date: event.event_date || "",
      location: event.location,
      participants_count: event.participants_count || 0,
      highlights: event.highlights || "",
      notes: event.notes || "",
      role: event.role || "lead",
      role_assignee: event.role_assignee || "",
      priority: event.priority || "medium",
      follow_up_status: event.follow_up_status || "open",
      category_tags: event.category_tags || [],
      linked_goals: event.linked_goals || [],
      production_items: event.production_items || [],
      suggested_competencies: event.suggested_competencies || [],
      recurrence: event.recurrence || initialFormData.recurrence
    });
    setIsEditing(true);
    setFormTab("details");
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || !selectedEvent) return;
    
    for (const file of files) {
      const uploadData = new FormData();
      uploadData.append("file", file);
      
      try {
        await axios.post(`${API}/events/${selectedEvent.id}/attachments`, uploadData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    fetchEvents();
    // Refresh selected event
    const updated = await axios.get(`${API}/events/${selectedEvent.id}`);
    setSelectedEvent(updated.data);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!selectedEvent) return;
    try {
      await axios.delete(`${API}/events/${selectedEvent.id}/attachments/${attachmentId}`);
      toast.success("Attachment deleted");
      fetchEvents();
      // Refresh selected event
      const updated = await axios.get(`${API}/events/${selectedEvent.id}`);
      setSelectedEvent(updated.data);
    } catch (error) {
      toast.error("Failed to delete attachment");
    }
  };

  // Inline role update for participation events table
  const handleInlineRoleChange = async (eventId, newRole) => {
    try {
      await axios.put(`${API}/events/${eventId}`, { role: newRole });
      toast.success("Role updated");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  // Inline priority update
  const handleInlinePriorityChange = async (eventId, newPriority) => {
    try {
      await axios.put(`${API}/events/${eventId}`, { priority: newPriority });
      toast.success("Priority updated");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to update priority");
    }
  };

  // Inline follow-up status update
  const handleInlineFollowUpChange = async (eventId, newStatus) => {
    try {
      await axios.put(`${API}/events/${eventId}`, { follow_up_status: newStatus });
      toast.success("Follow-up status updated");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to update follow-up status");
    }
  };

  // Share event functions
  const openShareDialog = (event) => {
    setShareEvent(event);
    setSelectedShareUsers(event.shared_with || []);
    setShareDialogOpen(true);
  };

  const handleShare = async () => {
    if (!shareEvent) return;
    setShareLoading(true);
    try {
      await axios.post(`${API}/events/${shareEvent.id}/share`, { user_ids: selectedShareUsers });
      toast.success(`Event shared with ${selectedShareUsers.length} user(s)`);
      setShareDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to share event");
    } finally {
      setShareLoading(false);
    }
  };

  const toggleShareUser = (userId) => {
    setSelectedShareUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedEvent(null);
    setIsEditing(false);
    setCategoryTagInput("");
    setFormTab("details");
  };

  // Production Item Functions
  const addProductionItem = () => {
    const newItem = {
      ...initialProductionItem,
      id: `temp-${Date.now()}`
    };
    setFormData({
      ...formData,
      production_items: [...formData.production_items, newItem]
    });
  };

  const updateProductionItem = (index, field, value) => {
    const updatedItems = [...formData.production_items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...formData, production_items: updatedItems });
  };

  const removeProductionItem = (index) => {
    const updatedItems = formData.production_items.filter((_, i) => i !== index);
    setFormData({ ...formData, production_items: updatedItems });
  };

  const handleProductionTitleSelect = (index, selectedTitle) => {
    // Find the previous entry with this title
    const previousEntry = productionTitles.find(t => t.title === selectedTitle);
    if (previousEntry) {
      const updatedItems = [...formData.production_items];
      updatedItems[index] = {
        ...updatedItems[index],
        title: previousEntry.title,
        quantity: previousEntry.quantity || "",
        lead: previousEntry.lead || ""
        // due_date and notes remain empty for new entry
      };
      setFormData({ ...formData, production_items: updatedItems });
    }
  };

  const getFilteredTitles = (currentTitle) => {
    if (!currentTitle) return productionTitles;
    return productionTitles.filter(t => 
      t.title.toLowerCase().includes(currentTitle.toLowerCase())
    );
  };

  const addCategoryTag = () => {
    const trimmedTag = categoryTagInput.trim();
    if (trimmedTag && !formData.category_tags.includes(trimmedTag)) {
      setFormData({
        ...formData,
        category_tags: [...formData.category_tags, trimmedTag]
      });
      setCategoryTagInput("");
    }
  };

  const toggleCategoryTag = (tag) => {
    if (formData.category_tags.includes(tag)) {
      setFormData({
        ...formData,
        category_tags: formData.category_tags.filter(t => t !== tag)
      });
    } else {
      setFormData({
        ...formData,
        category_tags: [...formData.category_tags, tag]
      });
    }
  };

  const removeCategoryTag = (tag) => {
    setFormData({
      ...formData,
      category_tags: formData.category_tags.filter(t => t !== tag)
    });
  };

  const getRoleBadge = (role) => {
    const styles = {
      lead: "bg-purple-100 text-purple-700",
      contributor: "bg-blue-100 text-blue-700",
      collaborator: "bg-emerald-100 text-emerald-700",
      assistant: "bg-amber-100 text-amber-700",
      attendee: "bg-stone-100 text-stone-700",
      consultant: "bg-cyan-100 text-cyan-700"
    };
    return styles[role] || styles.attendee;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: "bg-slate-100 text-slate-700",
      medium: "bg-amber-100 text-amber-700",
      high: "bg-red-100 text-red-700"
    };
    return styles[priority] || styles.medium;
  };

  const getFollowUpBadge = (status) => {
    const styles = {
      open: "bg-blue-100 text-blue-700",
      in_progress: "bg-amber-100 text-amber-700",
      waiting: "bg-purple-100 text-purple-700",
      complete: "bg-emerald-100 text-emerald-700",
      closed: "bg-stone-100 text-stone-700"
    };
    return styles[status] || styles.open;
  };

  const getCategoryBadge = (tag) => {
    const styles = {
      "Patron Interaction": "bg-blue-100 text-blue-700",
      "Incident": "bg-red-100 text-red-700",
      "Technology": "bg-purple-100 text-purple-700",
      "Project": "bg-emerald-100 text-emerald-700",
      "Educational Opportunity": "bg-amber-100 text-amber-700",
      "Meeting": "bg-stone-100 text-stone-700",
      "BAL": "bg-cyan-100 text-cyan-700",
      "Collection": "bg-pink-100 text-pink-700",
      "Policy": "bg-orange-100 text-orange-700",
      "Outreach": "bg-teal-100 text-teal-700",
      "Other": "bg-gray-100 text-gray-700"
    };
    return styles[tag] || "bg-gray-100 text-gray-700";
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      const hours = String(date.getHours()).padStart(2, '0');
      const mins = String(date.getMinutes()).padStart(2, '0');
      return `${month}/${day}/${year} ${hours}:${mins}`;
    } catch {
      return timestamp;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = activeTab === "all" || event.event_type === activeTab;
    return matchesSearch && matchesType;
  });

  // Sorting functions
  const sortParticipationEvents = (eventsList) => {
    const sorted = [...eventsList];
    switch (participationSort) {
      case "event_date":
        return sorted.sort((a, b) => {
          const dateA = a.event_date || a.date || "";
          const dateB = b.event_date || b.date || "";
          return dateB.localeCompare(dateA); // Newest first
        });
      case "role":
        return sorted.sort((a, b) => (a.role || "").localeCompare(b.role || ""));
      case "follow_up":
        const statusOrder = { open: 0, in_progress: 1, waiting: 2, complete: 3, closed: 4 };
        return sorted.sort((a, b) => 
          (statusOrder[a.follow_up_status] || 0) - (statusOrder[b.follow_up_status] || 0)
        );
      default:
        return sorted;
    }
  };

  const sortNoteworthyEvents = (eventsList) => {
    const sorted = [...eventsList];
    switch (noteworthySort) {
      case "newest":
        return sorted.sort((a, b) => {
          const dateA = a.created_at || "";
          const dateB = b.created_at || "";
          return dateB.localeCompare(dateA); // Newest first
        });
      case "date":
        return sorted.sort((a, b) => {
          const dateA = a.event_date || a.date || "";
          const dateB = b.event_date || b.date || "";
          return dateB.localeCompare(dateA);
        });
      case "priority":
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return sorted.sort((a, b) => 
          (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1)
        );
      case "status":
        const statusOrder = { open: 0, in_progress: 1, waiting: 2, complete: 3, closed: 4 };
        return sorted.sort((a, b) => 
          (statusOrder[a.follow_up_status] || 0) - (statusOrder[b.follow_up_status] || 0)
        );
      default:
        return sorted;
    }
  };

  const participationEvents = sortParticipationEvents(
    filteredEvents.filter(e => e.event_type === "participation")
  );
  const noteworthyEvents = sortNoteworthyEvents(
    filteredEvents.filter(e => e.event_type === "noteworthy")
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="events-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const ParticipationTable = ({ events }) => (
    <Card className="border-stone-200">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-medium text-sm text-muted-foreground">
          {events.length} Participation Event{events.length !== 1 ? 's' : ''}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort by:</span>
          <Select value={participationSort} onValueChange={setParticipationSort}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="participation-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="event_date">Event Date</SelectItem>
              <SelectItem value="role">Role</SelectItem>
              <SelectItem value="follow_up">Follow-up Status</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event/Program Name</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Follow-up Status</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Organizer</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No participation events found
              </TableCell>
            </TableRow>
          ) : (
            events.map((event) => (
              <TableRow key={event.id} className="table-row-hover" data-testid={`event-row-${event.id}`}>
                <TableCell>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{event.title}</p>
                      {event.shared_with?.includes(user?.id) && event.created_by !== user?.id && (
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                          <Share2 className="h-3 w-3 mr-1" />
                          Shared
                        </Badge>
                      )}
                      {/* Competency Status Indicator */}
                      {event.suggested_competencies?.length > 0 && (
                        event.competency_status === "approved" ? (
                          <Badge className="text-xs bg-green-100 text-green-700 border-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Competencies Approved
                          </Badge>
                        ) : event.competency_status === "rejected" ? (
                          <Badge className="text-xs bg-red-100 text-red-700 border-red-200">
                            <XCircle className="h-3 w-3 mr-1" />
                            Competencies Rejected
                          </Badge>
                        ) : (
                          <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Pending Review
                          </Badge>
                        )
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{event.location}</p>
                    {isShowingAllData && getStaffName(event.created_by) && (
                      <p className="text-xs text-blue-600 mt-1">
                        Staff: {getStaffName(event.created_by)}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {event.event_date || event.date || "N/A"}
                </TableCell>
                <TableCell>
                  <Select
                    value={event.priority || "medium"}
                    onValueChange={(value) => handleInlinePriorityChange(event.id, value)}
                  >
                    <SelectTrigger className={`w-28 h-8 text-xs ${getPriorityBadge(event.priority)}`} data-testid={`priority-select-${event.id}`}>
                      <SelectValue>
                        {PRIORITIES.find(p => p.value === event.priority)?.label || "Medium"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    value={event.follow_up_status || "open"}
                    onValueChange={(value) => handleInlineFollowUpChange(event.id, value)}
                  >
                    <SelectTrigger className={`w-32 h-8 text-xs ${getFollowUpBadge(event.follow_up_status)}`} data-testid={`followup-select-${event.id}`}>
                      <SelectValue>
                        {FOLLOW_UP_STATUSES.find(s => s.value === event.follow_up_status)?.label || "Open"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOW_UP_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    value={event.role || "lead"}
                    onValueChange={(value) => handleInlineRoleChange(event.id, value)}
                  >
                    <SelectTrigger className={`w-32 h-8 text-xs ${getRoleBadge(event.role)}`} data-testid={`role-select-${event.id}`}>
                      <SelectValue>
                        {ROLES.find(r => r.value === event.role)?.label || "Lead"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm">
                  {event.role_assignee || "-"}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`event-menu-${event.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(event)}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      {event.created_by === user?.id && (
                        <DropdownMenuItem onClick={() => openShareDialog(event)}>
                          <Share2 className="mr-2 h-4 w-4" /> Share
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => {
                          setSelectedEvent(event);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );

  const NoteworthyTable = ({ events }) => (
    <Card className="border-stone-200">
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-medium text-sm text-muted-foreground">
          {events.length} Noteworthy Event{events.length !== 1 ? 's' : ''}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort by:</span>
          <Select value={noteworthySort} onValueChange={setNoteworthySort}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="noteworthy-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="date">Event Date</SelectItem>
              <SelectItem value="priority">Priority</SelectItem>
              <SelectItem value="status">Follow-up Status</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Event Name</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Follow-up Status</TableHead>
            <TableHead>Category Tags</TableHead>
            <TableHead>Files</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                No noteworthy events found
              </TableCell>
            </TableRow>
          ) : (
            events.map((event) => (
              <TableRow key={event.id} className="table-row-hover" data-testid={`event-row-${event.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{event.title}</p>
                        {event.shared_with?.includes(user?.id) && event.created_by !== user?.id && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            <Share2 className="h-3 w-3 mr-1" />
                            Shared
                          </Badge>
                        )}
                        {/* Competency Status Indicator */}
                        {event.suggested_competencies?.length > 0 && (
                          event.competency_status === "approved" ? (
                            <Badge className="text-xs bg-green-100 text-green-700 border-green-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approved
                            </Badge>
                          ) : event.competency_status === "rejected" ? (
                            <Badge className="text-xs bg-red-100 text-red-700 border-red-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Rejected
                            </Badge>
                          ) : (
                            <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{event.location}</p>
                      {isShowingAllData && getStaffName(event.created_by) && (
                        <p className="text-xs text-blue-600 mt-1">
                          Staff: {getStaffName(event.created_by)}
                        </p>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {event.event_date || event.date} {event.time && `${event.time}`}
                </TableCell>
                <TableCell>
                  <Badge className={getPriorityBadge(event.priority)}>
                    {PRIORITIES.find(p => p.value === event.priority)?.label || "Medium"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className={getFollowUpBadge(event.follow_up_status)}>
                    {FOLLOW_UP_STATUSES.find(s => s.value === event.follow_up_status)?.label || "Open"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {event.category_tags && event.category_tags.slice(0, 2).map((tag, i) => (
                      <Badge key={i} className={`text-xs ${getCategoryBadge(tag)}`}>
                        {tag}
                      </Badge>
                    ))}
                    {event.category_tags && event.category_tags.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{event.category_tags.length - 2}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {event.attachments && event.attachments.length > 0 ? (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <FileIcon className="h-3 w-3" />
                      <span>{event.attachments.length}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`event-menu-${event.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(event)}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                      </DropdownMenuItem>
                      {event.created_by === user?.id && (
                        <DropdownMenuItem onClick={() => openShareDialog(event)}>
                          <Share2 className="mr-2 h-4 w-4" /> Share
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => {
                          setSelectedEvent(event);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="events-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            Events / Programs
          </h1>
          <p className="text-muted-foreground mt-1">
            Track participation and noteworthy events
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const response = await axios.get(`${API}/events/export/csv`, {
                  responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `events_export_${new Date().toISOString().slice(0,10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Events exported successfully");
              } catch (error) {
                toast.error("Failed to export events");
              }
            }}
            data-testid="export-events-csv"
          >
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            data-testid="create-event-btn"
            className="btn-animate"
          >
            <Plus className="mr-2 h-4 w-4" /> New Event
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search events..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-events"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList data-testid="event-tabs">
          <TabsTrigger value="all" data-testid="tab-all">
            All Events ({filteredEvents.length})
          </TabsTrigger>
          <TabsTrigger value="participation" data-testid="tab-participation">
            Participation ({participationEvents.length})
          </TabsTrigger>
          <TabsTrigger value="noteworthy" data-testid="tab-noteworthy">
            Noteworthy ({noteworthyEvents.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6 space-y-6">
          {participationEvents.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Participation Events</h3>
              <ParticipationTable events={participationEvents} />
            </div>
          )}
          {noteworthyEvents.length > 0 && (
            <div>
              <h3 className="font-medium mb-3">Noteworthy Events</h3>
              <NoteworthyTable events={noteworthyEvents} />
            </div>
          )}
          {filteredEvents.length === 0 && (
            <Card className="border-stone-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
                <h3 className="font-medium text-lg">No events found</h3>
                <p className="text-muted-foreground text-sm mt-1">Create your first event</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="participation" className="mt-6">
          <ParticipationTable events={participationEvents} />
        </TabsContent>

        <TabsContent value="noteworthy" className="mt-6">
          <NoteworthyTable events={noteworthyEvents} />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {isEditing ? "Edit Event" : "Create New Event"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update the event details" : "Record a new library event"}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={formTab} onValueChange={setFormTab} className="w-full">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="schedule">
                <Repeat className="h-3 w-3 mr-1" /> Schedule
              </TabsTrigger>
              <TabsTrigger value="production">
                Production
              </TabsTrigger>
              <TabsTrigger value="categories">
                Tags <Tag className="ml-1 h-3 w-3" />
              </TabsTrigger>
              <TabsTrigger value="competencies">
                Competencies
              </TabsTrigger>
              <TabsTrigger value="files" disabled={!isEditing}>
                Files {isEditing && <FileIcon className="ml-1 h-3 w-3" />}
              </TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <TabsContent value="details" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Event Details</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="event_type">Event Type *</Label>
                      <Select
                        value={formData.event_type}
                        onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                      >
                        <SelectTrigger data-testid="event-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EVENT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="title">Event/Program Name *</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Event name"
                        required
                        data-testid="event-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Describe the event..."
                        rows={3}
                        required
                        data-testid="event-description-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Date & Location</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Proposed Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                        data-testid="event-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="event_date">Event Date (Deadline) *</Label>
                      <Input
                        id="event_date"
                        type="date"
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        data-testid="event-deadline-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Actual event date - determines to-do due dates
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label htmlFor="time">Time</Label>
                      <Input
                        id="time"
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        data-testid="event-time-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="location">Location *</Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        placeholder="e.g., Main Reading Room"
                        required
                        data-testid="event-location-input"
                      />
                    </div>
                  </div>
                </div>

                {formData.event_type === "participation" && (
                  <>
                    <div className="form-section">
                      <h3 className="font-medium mb-3">Role Assignment</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="role">Role *</Label>
                          <Select
                            value={formData.role}
                            onValueChange={(value) => setFormData({ ...formData, role: value })}
                          >
                            <SelectTrigger data-testid="event-role-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLES.map((role) => (
                                <SelectItem key={role.value} value={role.value}>
                                  {role.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="role_assignee">Organizer Name</Label>
                          <Input
                            id="role_assignee"
                            value={formData.role_assignee}
                            onChange={(e) => setFormData({ ...formData, role_assignee: e.target.value })}
                            placeholder="Event organizer name"
                            data-testid="event-organizer-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-section">
                      <h3 className="font-medium mb-3">Priority & Status</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="priority">Priority *</Label>
                          <Select
                            value={formData.priority}
                            onValueChange={(value) => setFormData({ ...formData, priority: value })}
                          >
                            <SelectTrigger data-testid="event-priority-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PRIORITIES.map((priority) => (
                                <SelectItem key={priority.value} value={priority.value}>
                                  {priority.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="follow_up_status">Follow-up Status *</Label>
                          <Select
                            value={formData.follow_up_status}
                            onValueChange={(value) => setFormData({ ...formData, follow_up_status: value })}
                          >
                            <SelectTrigger data-testid="event-follow-up-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FOLLOW_UP_STATUSES.map((status) => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="form-section">
                      <h3 className="font-medium mb-3">Participation</h3>
                      <div>
                        <Label htmlFor="participants_count">Number of Participants</Label>
                        <Input
                          id="participants_count"
                          type="number"
                          min="0"
                          value={formData.participants_count}
                          onChange={(e) => setFormData({ ...formData, participants_count: e.target.value })}
                          placeholder="0"
                          data-testid="event-participants-input"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="highlights">Highlights</Label>
                  <Textarea
                    id="highlights"
                    value={formData.highlights}
                    onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                    placeholder="Key highlights and achievements..."
                    rows={2}
                    data-testid="event-highlights-input"
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    data-testid="event-notes-input"
                  />
                </div>

                {/* Priority & Status Section - For All Events */}
                {formData.event_type === "noteworthy" && (
                  <div className="form-section">
                    <h3 className="font-medium mb-3">Priority & Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="priority">Priority *</Label>
                        <Select
                          value={formData.priority}
                          onValueChange={(value) => setFormData({ ...formData, priority: value })}
                        >
                          <SelectTrigger data-testid="event-priority-select-noteworthy">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PRIORITIES.map((priority) => (
                              <SelectItem key={priority.value} value={priority.value}>
                                {priority.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="follow_up_status">Follow-up Status *</Label>
                        <Select
                          value={formData.follow_up_status}
                          onValueChange={(value) => setFormData({ ...formData, follow_up_status: value })}
                        >
                          <SelectTrigger data-testid="event-follow-up-select-noteworthy">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FOLLOW_UP_STATUSES.map((status) => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Linked Goals Section - For All Events */}
                <div className="form-section">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Linked Goals
                  </h3>
                  
                  {/* Selected Goals */}
                  {formData.linked_goals.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.linked_goals.map((goalId) => {
                        const goal = goals.find(g => g.id === goalId);
                        return goal ? (
                          <Badge
                            key={goalId}
                            className="px-3 py-1 bg-emerald-100 text-emerald-700"
                          >
                            {goal.title}
                            <button
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                linked_goals: formData.linked_goals.filter(id => id !== goalId)
                              })}
                              className="ml-2 hover:text-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  
                  {/* Goals Dropdown */}
                  {goals.length > 0 ? (
                    <>
                      <p className="text-xs text-muted-foreground mb-2">Select goals to link:</p>
                      <div className="flex flex-wrap gap-2">
                        {goals.filter(goal => !formData.linked_goals.includes(goal.id)).map((goal) => (
                          <Badge
                            key={goal.id}
                            variant="outline"
                            className="cursor-pointer hover:bg-emerald-50 hover:border-emerald-300"
                            onClick={() => setFormData({
                              ...formData,
                              linked_goals: [...formData.linked_goals, goal.id]
                            })}
                            data-testid={`link-goal-${goal.id}`}
                          >
                            + {goal.title}
                          </Badge>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">No yearly goals available. Create goals in the Goals page first.</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="schedule" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Repeat className="h-4 w-4" />
                    Recurrence Pattern
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Set up this event to repeat on a schedule
                  </p>
                  
                  {/* Recurrence Type */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="recurrence_type">Repeat</Label>
                      <Select
                        value={formData.recurrence?.type || "none"}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          recurrence: { ...formData.recurrence, type: value }
                        })}
                      >
                        <SelectTrigger data-testid="event-recurrence-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Weekly Options */}
                    {formData.recurrence?.type === "weekly" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Repeat every</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="12"
                            className="w-20"
                            value={formData.recurrence?.interval || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, interval: parseInt(e.target.value) || 1 }
                            })}
                            data-testid="event-recurrence-interval"
                          />
                          <span className="text-sm text-muted-foreground">week(s) on:</span>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 mt-2">
                          {DAYS_OF_WEEK.map((day) => (
                            <Badge
                              key={day.value}
                              variant={formData.recurrence?.days_of_week?.includes(day.value) ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => {
                                const currentDays = formData.recurrence?.days_of_week || [];
                                const newDays = currentDays.includes(day.value)
                                  ? currentDays.filter(d => d !== day.value)
                                  : [...currentDays, day.value];
                                setFormData({
                                  ...formData,
                                  recurrence: { ...formData.recurrence, days_of_week: newDays }
                                });
                              }}
                              data-testid={`event-day-${day.value}`}
                            >
                              {day.label.slice(0, 3)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Monthly - Day of Week Options */}
                    {formData.recurrence?.type === "monthly_day_of_week" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Repeat on the</Label>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Select
                            value={formData.recurrence?.week_of_month || "first"}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, week_of_month: value }
                            })}
                          >
                            <SelectTrigger className="w-32" data-testid="event-week-of-month-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {WEEK_OF_MONTH_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <Select
                            value={formData.recurrence?.day_of_week || "friday"}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, day_of_week: value }
                            })}
                          >
                            <SelectTrigger className="w-36" data-testid="event-day-of-week-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DAYS_OF_WEEK.map((day) => (
                                <SelectItem key={day.value} value={day.value}>
                                  {day.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <span className="text-sm text-muted-foreground">of each month</span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-muted-foreground">Every</span>
                          <Input
                            type="number"
                            min="1"
                            max="12"
                            className="w-20"
                            value={formData.recurrence?.interval || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, interval: parseInt(e.target.value) || 1 }
                            })}
                          />
                          <span className="text-sm text-muted-foreground">month(s)</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Monthly - Day of Month Options */}
                    {formData.recurrence?.type === "monthly_day_of_month" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Repeat on day</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="31"
                            className="w-20"
                            value={formData.recurrence?.day_of_month || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, day_of_month: parseInt(e.target.value) || 1 }
                            })}
                            data-testid="event-day-of-month-input"
                          />
                          <span className="text-sm text-muted-foreground">of each month</span>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-muted-foreground">Every</span>
                          <Input
                            type="number"
                            min="1"
                            max="12"
                            className="w-20"
                            value={formData.recurrence?.interval || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, interval: parseInt(e.target.value) || 1 }
                            })}
                          />
                          <span className="text-sm text-muted-foreground">month(s)</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Custom Dates */}
                    {formData.recurrence?.type === "custom" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>Custom Dates</Label>
                        <p className="text-xs text-muted-foreground">
                          Add specific dates for this event
                        </p>
                        <div className="flex gap-2">
                          <Input
                            type="date"
                            id="event-custom-date-input"
                            data-testid="event-custom-date-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const input = document.getElementById('event-custom-date-input');
                              if (input?.value) {
                                const currentDates = formData.recurrence?.custom_dates || [];
                                if (!currentDates.includes(input.value)) {
                                  setFormData({
                                    ...formData,
                                    recurrence: { ...formData.recurrence, custom_dates: [...currentDates, input.value] }
                                  });
                                }
                                input.value = '';
                              }
                            }}
                            data-testid="add-event-custom-date-btn"
                          >
                            Add Date
                          </Button>
                        </div>
                        {formData.recurrence?.custom_dates?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {formData.recurrence.custom_dates.map((date, idx) => (
                              <Badge key={idx} variant="secondary" className="px-3 py-1">
                                {date}
                                <button
                                  type="button"
                                  className="ml-2 hover:text-red-600"
                                  onClick={() => setFormData({
                                    ...formData,
                                    recurrence: {
                                      ...formData.recurrence,
                                      custom_dates: formData.recurrence.custom_dates.filter((_, i) => i !== idx)
                                    }
                                  })}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* End Conditions */}
                    {formData.recurrence?.type && formData.recurrence.type !== "none" && formData.recurrence.type !== "custom" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-muted/20">
                        <Label>End After</Label>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              id="event-end-occurrences"
                              name="event-end-type"
                              checked={formData.recurrence?.end_type === "occurrences"}
                              onChange={() => setFormData({
                                ...formData,
                                recurrence: { ...formData.recurrence, end_type: "occurrences" }
                              })}
                            />
                            <Label htmlFor="event-end-occurrences" className="font-normal">After</Label>
                            <Input
                              type="number"
                              min="1"
                              max="52"
                              className="w-20"
                              value={formData.recurrence?.occurrences || 4}
                              onChange={(e) => setFormData({
                                ...formData,
                                recurrence: { ...formData.recurrence, occurrences: parseInt(e.target.value) || 1 }
                              })}
                              disabled={formData.recurrence?.end_type !== "occurrences"}
                              data-testid="event-occurrences-input"
                            />
                            <span className="text-sm text-muted-foreground">occurrence(s)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            id="event-end-date"
                            name="event-end-type"
                            checked={formData.recurrence?.end_type === "end_date"}
                            onChange={() => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, end_type: "end_date" }
                            })}
                          />
                          <Label htmlFor="event-end-date" className="font-normal">By date</Label>
                          <Input
                            type="date"
                            className="w-40"
                            value={formData.recurrence?.end_date || ""}
                            onChange={(e) => setFormData({
                              ...formData,
                              recurrence: { ...formData.recurrence, end_date: e.target.value }
                            })}
                            disabled={formData.recurrence?.end_type !== "end_date"}
                            data-testid="event-end-date-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Info about generating recurring instances */}
                {formData.recurrence?.type && formData.recurrence.type !== "none" && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    <p className="font-medium flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      How Recurring Events Work
                    </p>
                    <p className="mt-1 text-blue-600">
                      After saving, use the "Generate Dates" option from the event menu to create all the recurring instances.
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="production" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Production & Projects</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add production items needed for this event. Previous entries will auto-suggest when typing.
                  </p>
                  
                  {/* Production Items List */}
                  {formData.production_items.length > 0 && (
                    <div className="space-y-4 mb-4">
                      {formData.production_items.map((item, index) => (
                        <div key={item.id || index} className="p-4 border rounded-lg bg-muted/20 space-y-3">
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium text-muted-foreground">Item {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeProductionItem(index)}
                              className="text-red-600 hover:text-red-700 h-6 px-2"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="relative">
                              <Label className="text-xs">Title *</Label>
                              <Input
                                value={item.title}
                                onChange={(e) => updateProductionItem(index, 'title', e.target.value)}
                                placeholder="e.g., Book Bags, Career Packets"
                                list={`production-titles-${index}`}
                                data-testid={`production-title-${index}`}
                              />
                              <datalist id={`production-titles-${index}`}>
                                {getFilteredTitles(item.title).map((t, i) => (
                                  <option key={i} value={t.title} />
                                ))}
                              </datalist>
                              {/* Auto-fill button when matching title found */}
                              {item.title && productionTitles.find(t => t.title.toLowerCase() === item.title.toLowerCase()) && (
                                <Button
                                  type="button"
                                  variant="link"
                                  size="sm"
                                  className="text-xs p-0 h-auto mt-1"
                                  onClick={() => handleProductionTitleSelect(index, item.title)}
                                >
                                  Auto-fill from previous entry
                                </Button>
                              )}
                            </div>
                            <div>
                              <Label className="text-xs">Quantity</Label>
                              <Input
                                type="number"
                                value={item.quantity || ""}
                                onChange={(e) => updateProductionItem(index, 'quantity', e.target.value ? parseInt(e.target.value) : "")}
                                placeholder="Optional"
                                data-testid={`production-qty-${index}`}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Due Date</Label>
                              <Input
                                type="date"
                                value={item.due_date}
                                onChange={(e) => updateProductionItem(index, 'due_date', e.target.value)}
                                data-testid={`production-date-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Lead</Label>
                              <Input
                                value={item.lead}
                                onChange={(e) => updateProductionItem(index, 'lead', e.target.value)}
                                placeholder="Person responsible"
                                data-testid={`production-lead-${index}`}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-xs">Notes</Label>
                            <Textarea
                              value={item.notes}
                              onChange={(e) => updateProductionItem(index, 'notes', e.target.value)}
                              placeholder="Additional notes..."
                              rows={2}
                              data-testid={`production-notes-${index}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Production Item Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addProductionItem}
                    className="w-full"
                    data-testid="add-production-item-btn"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Production Item
                  </Button>
                  
                  {formData.production_items.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center mt-4">
                      No production items added yet. Click the button above to add items like book bags, career packets, etc.
                    </p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="categories" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Category Tags</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Categorize this event for reporting and tracking
                  </p>
                  
                  {/* Selected Tags */}
                  {formData.category_tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {formData.category_tags.map((tag) => (
                        <Badge
                          key={tag}
                          className={`px-3 py-1 ${getCategoryBadge(tag)}`}
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeCategoryTag(tag)}
                            className="ml-2 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Custom Tag Input */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="Add custom category tag..."
                      value={categoryTagInput}
                      onChange={(e) => setCategoryTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addCategoryTag())}
                      data-testid="category-tag-input"
                    />
                    <Button type="button" variant="outline" onClick={addCategoryTag} data-testid="add-category-tag-btn">
                      Add Tag
                    </Button>
                  </div>
                  
                  {/* Preset Tags */}
                  <p className="text-xs text-muted-foreground mb-2">Quick add from presets:</p>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_CATEGORY_TAGS.filter(tag => !formData.category_tags.includes(tag)).map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => toggleCategoryTag(tag)}
                        data-testid={`category-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        + {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="competencies" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Suggest Competencies</h3>
                  
                  {/* Show approval status when editing */}
                  {isEditing && selectedEvent?.suggested_competencies?.length > 0 && (
                    <div className={`p-4 rounded-lg mb-4 ${
                      selectedEvent?.competency_status === "approved" 
                        ? "bg-green-50 border border-green-200"
                        : selectedEvent?.competency_status === "rejected"
                        ? "bg-red-50 border border-red-200"
                        : "bg-amber-50 border border-amber-200"
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {selectedEvent?.competency_status === "approved" ? (
                          <>
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <span className="font-medium text-green-800">Competencies Approved</span>
                          </>
                        ) : selectedEvent?.competency_status === "rejected" ? (
                          <>
                            <XCircle className="h-5 w-5 text-red-600" />
                            <span className="font-medium text-red-800">Competencies Rejected</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="h-5 w-5 text-amber-600" />
                            <span className="font-medium text-amber-800">Pending Supervisor Review</span>
                          </>
                        )}
                      </div>
                      {selectedEvent?.competency_status === "approved" && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedEvent.suggested_competencies.map((comp) => (
                            <Badge key={comp} className="bg-green-100 text-green-700">
                              {comp}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {selectedEvent?.competency_status === "rejected" && selectedEvent?.competency_notes && (
                        <p className="text-sm text-red-700 mt-2">
                          <strong>Reason:</strong> {selectedEvent.competency_notes}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground mb-4">
                    Select the competencies this event demonstrates. Your supervisor will review and approve these suggestions.
                  </p>
                  
                  {/* Essential Functions */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-purple-700 mb-3">
                      I. Essential Functions
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {ESSENTIAL_FUNCTIONS.map((func) => {
                        const isSelected = formData.suggested_competencies?.includes(func.value);
                        return (
                          <button
                            key={func.value}
                            type="button"
                            onClick={() => {
                              const current = formData.suggested_competencies || [];
                              if (isSelected) {
                                setFormData({
                                  ...formData,
                                  suggested_competencies: current.filter(c => c !== func.value)
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  suggested_competencies: [...current, func.value]
                                });
                              }
                            }}
                            className={`text-left p-3 rounded-lg border transition-all ${
                              isSelected 
                                ? "border-purple-500 bg-purple-50 text-purple-800" 
                                : "border-gray-200 hover:border-purple-300 hover:bg-purple-50/50"
                            }`}
                            data-testid={`competency-${func.value.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                isSelected ? "bg-purple-500 border-purple-500" : "border-gray-300"
                              }`}>
                                {isSelected && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <span className="text-sm font-medium">{func.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* LA Competencies */}
                  <div>
                    <h4 className="text-sm font-medium text-blue-700 mb-3">
                      II. LA Competencies
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {LA_COMPETENCIES.map((comp) => {
                        const isSelected = formData.suggested_competencies?.includes(comp.value);
                        return (
                          <button
                            key={comp.value}
                            type="button"
                            onClick={() => {
                              const current = formData.suggested_competencies || [];
                              if (isSelected) {
                                setFormData({
                                  ...formData,
                                  suggested_competencies: current.filter(c => c !== comp.value)
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  suggested_competencies: [...current, comp.value]
                                });
                              }
                            }}
                            className={`text-left p-3 rounded-lg border transition-all ${
                              isSelected 
                                ? "border-blue-500 bg-blue-50 text-blue-800" 
                                : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50"
                            }`}
                            data-testid={`competency-${comp.value.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                                isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"
                              }`}>
                                {isSelected && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <span className="text-sm font-medium">{comp.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Selected Competencies Summary */}
                  {formData.suggested_competencies?.length > 0 && (
                    <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="text-sm font-medium text-amber-800 mb-2">
                        Selected Competencies ({formData.suggested_competencies.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {formData.suggested_competencies.map((comp) => (
                          <Badge 
                            key={comp} 
                            className={`${
                              ESSENTIAL_FUNCTIONS.some(f => f.value === comp)
                                ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            } cursor-pointer`}
                            onClick={() => {
                              setFormData({
                                ...formData,
                                suggested_competencies: formData.suggested_competencies.filter(c => c !== comp)
                              });
                            }}
                          >
                            {comp} ×
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-amber-600 mt-2">
                        These will be submitted for supervisor review
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="files" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Supporting Files</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Upload documents, images, or other files related to this event
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        data-testid="event-file-upload-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="upload-event-file-btn"
                      >
                        <Upload className="mr-2 h-4 w-4" /> Upload Files
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">All file types accepted</p>
                    </div>
                    
                    {selectedEvent?.attachments && selectedEvent.attachments.length > 0 && (
                      <div className="space-y-2">
                        {selectedEvent.attachments.map((attachment) => (
                          <div key={attachment.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileIcon className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">{attachment.original_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(attachment.file_size / 1024).toFixed(1)} KB • {formatDate(attachment.uploaded_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(getAttachmentUrl(attachment), '_blank')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAttachment(attachment.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {(!selectedEvent?.attachments || selectedEvent.attachments.length === 0) && (
                      <div className="text-center py-8 bg-muted/20 rounded-lg">
                        <FileIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">No files uploaded yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-event-btn">
                  {isEditing ? "Update Event" : "Create Event"}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedEvent?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-event"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-md" data-testid="share-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="share-event-title">
              <Share2 className="h-5 w-5 text-blue-600" />
              Share Event
            </DialogTitle>
            <DialogDescription>
              Allow other staff members to edit and add files to "{shareEvent?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm font-medium mb-3 block">Select users to share with:</Label>
            <div className="max-h-64 overflow-y-auto space-y-2 border rounded-md p-3" data-testid="share-user-list">
              {allUsers.filter(u => u.id !== user?.id).map((u) => (
                <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-muted rounded cursor-pointer" data-testid={`share-user-${u.id}`}>
                  <input
                    type="checkbox"
                    checked={selectedShareUsers.includes(u.id)}
                    onChange={() => toggleShareUser(u.id)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <div>
                    <p className="text-sm font-medium">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                </label>
              ))}
              {allUsers.filter(u => u.id !== user?.id).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No other users available</p>
              )}
            </div>
            {selectedShareUsers.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                Sharing with {selectedShareUsers.length} user(s)
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleShare} 
              disabled={shareLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="share-event-btn"
            >
              {shareLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sharing...
                </>
              ) : (
                <>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
